import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/api/auth.api';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ApiClientError } from '@/types/api';
import { toast } from '@/stores/uiStore';

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    password_confirmation: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => authApi.resetPassword({ token, ...values }),
    onSuccess: () => {
      toast.success('Password reset', 'You can now sign in with your new password.');
      navigate('/login', { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiClientError) setError('root', { message: error.message });
    },
  });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base px-4 text-center">
        <div>
          <p className="text-text-secondary">This reset link is missing a token.</p>
          <Link to="/forgot-password" className="mt-2 inline-block text-sm text-text-link hover:underline">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/vynzo-logo.svg" alt="Vynzo" className="h-12 w-12" />
          <h1 className="font-display text-2xl font-extrabold text-text-primary">Set a new password</h1>
        </div>

        <form
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          className="flex flex-col gap-5 rounded-2xl border border-border-subtle bg-bg-elevated p-8 shadow-xl"
        >
          {errors.root && (
            <div className="rounded-md border border-state-error/30 bg-state-error-subtle px-4 py-3 text-sm text-state-error">
              {errors.root.message}
            </div>
          )}
          <Input label="New password" type="password" error={errors.password?.message} {...register('password')} />
          <Input
            label="Confirm new password"
            type="password"
            error={errors.password_confirmation?.message}
            {...register('password_confirmation')}
          />
          <Button type="submit" loading={mutation.isPending} fullWidth>
            Reset password
          </Button>
        </form>
      </div>
    </div>
  );
}
